import { Link } from "react-router-dom";
import { ShoppingCart } from "lucide-react";
import { formatBrlCurrency } from "@/lib/price";
import type { PublicProduct } from "@/types/models";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";

export interface ProductCardProps {
  product: PublicProduct;
  storeSlug: string;
}

export default function ProductCard({ product, storeSlug }: ProductCardProps) {
  const { addItem, hasItem, canAddMore, isReady, getItemQuantity } = useCart();
  const thumbnailUrl = product.images[0]?.thumbnailUrl;
  const hasComparePrice =
    product.compareAtPriceCents != null &&
    product.compareAtPriceCents > product.priceCents;
  const inCart = hasItem(product.id);
  const quantity = getItemQuantity(product.id);

  return (
    <Link
      to={`/stores/${storeSlug}/products/${product.slug}`}
      className="group block border border-primary-200 dark:border-primary-800 overflow-hidden transition-colors hover:border-primary-400 dark:hover:border-primary-600"
    >
      <div className="aspect-square bg-primary-100 dark:bg-primary-900 overflow-hidden">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={product.name}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-sm font-light text-primary-400">
            Sem imagem
          </div>
        )}
      </div>
      <div className="p-4 space-y-1">
        <h2 className="text-base font-light text-primary-900 dark:text-white line-clamp-2">
          {product.name}
        </h2>
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap items-baseline gap-2 min-w-0">
            <span className="text-sm font-light text-primary-900 dark:text-white">
              {formatBrlCurrency(product.priceCents)}
            </span>
            {hasComparePrice && (
              <span className="text-sm font-light text-primary-400 line-through">
                {formatBrlCurrency(product.compareAtPriceCents!)}
              </span>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="relative shrink-0 h-8 w-8"
            disabled={!isReady || (!canAddMore && !inCart)}
            aria-label={
              inCart
                ? `Adicionar mais (${quantity} no carrinho)`
                : "Adicionar ao carrinho"
            }
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              addItem({
                productId: product.id,
                name: product.name,
                slug: product.slug,
                priceCents: product.priceCents,
                thumbnailUrl: thumbnailUrl ?? null,
              });
            }}
          >
            <ShoppingCart className="size-4" />
            {inCart && (
              <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-700 px-0.5 text-[10px] font-light text-white dark:bg-primary-300 dark:text-primary-950">
                {quantity}
              </span>
            )}
          </Button>
        </div>
      </div>
    </Link>
  );
}
